import React, { useState, useEffect, useRef } from "react";
import axios from "axios";

// ─── Ultra-Luxury Matte Obsidian & Champagne Palette ───
// Solid, clean, dark-mode luxury — NO heavy gradients, NO white UI
const THEME = {
  bg: "#0C0B10",          // Clean solid matte midnight noir
  cardBg: "#15141C",      // Solid matte dark graphite
  cardBorder: "rgba(212, 175, 55, 0.22)",
  cardBorderActive: "#D4AF37",
  gold: "#D4AF37",
  goldLight: "#F5E8C7",
  goldGlow: "rgba(212, 175, 55, 0.25)",
  textMain: "#FFFFFF",
  textSub: "rgba(255, 255, 255, 0.68)",
  textMuted: "rgba(255, 255, 255, 0.45)",
};

// ─── Verified Carnival Castle Real Celebration Stories ───
const REAL_STORIES = [
  {
    _id: "cc-rev-1",
    name: "Kanneboina Pravalika",
    location: "Kondapur",
    category: "screening",
    occasion: "Private Cinema Screening",
    occasionEmoji: "🎬",
    theatre: "Ruby Theatre",
    rating: 5,
    quote: "Amazing ambiance, excellent service, and everything was perfectly arranged. Highly recommended!",
    fullText: "Had a great time at Carnival Castle Private Theaters! 😊 Amazing ambiance, excellent service, and everything was perfectly arranged. Highly recommended for couples and family celebrations!",
    image: "https://api.carnivalcastle.com/uploads/testimoniImg/1779723776325-DSC_0916.JPG",
    avatar: "https://api.carnivalcastle.com/uploads/testimoniImg/1779723776326-unnamed (3).png",
  },
  {
    _id: "cc-rev-2",
    name: "Vasantha Gollamudi",
    location: "Kondapur",
    category: "birthday",
    occasion: "Akshaya's Birthday Party",
    occasionEmoji: "🎂",
    theatre: "Grand Sapphire",
    rating: 5,
    quote: "The private theater setup and overall arrangements made the party feel very special and fun-filled.",
    fullText: "We recently hosted my daughter Akshaya’s birthday party at Carnival Castle Private Theaters and it was a wonderful experience. The kids had an amazing time and enjoyed every moment. The staff was supportive and cooperative throughout the event, which made hosting stress-free for us. It was a memorable celebration for both kids and parents. Highly recommended! 🎉😊",
    image: "https://api.carnivalcastle.com/uploads/testimoniImg/1779723240522-DSC_0961.JPG",
    avatar: null,
  },
  {
    _id: "cc-rev-3",
    name: "Manjunath Swamy",
    location: "Kondapur",
    category: "birthday",
    occasion: "1st Wedding Anniversary",
    occasionEmoji: "💍",
    theatre: "Diamond Lounge",
    rating: 5,
    quote: "We commemorated our first wedding anniversary and enjoyed ourselves immensely.",
    fullText: "We commemorated our first wedding anniversary and enjoyed ourselves immensely. A highly positive team contributed to the overall enjoyment of our event, which was an incredible experience and is highly recommended for all types of gatherings with friends and family.",
    image: "https://api.carnivalcastle.com/uploads/testimoniImg/1779720831790-DSC02727.JPG",
    avatar: null,
  },
  {
    _id: "cc-rev-4",
    name: "Kamal",
    location: "Kondapur",
    category: "screening",
    occasion: "Private Movie Night",
    occasionEmoji: "🍿",
    theatre: "Platinum Cinema",
    rating: 5,
    quote: "The ambience, sound quality, and privacy were top-notch. Super comfortable seating.",
    fullText: "Just had an amazing experience at this private theater. The ambience, sound quality, and privacy were top-notch. Super comfortable seating and perfect for enjoying movies without any disturbance. Definitely one of the best theater experiences I’ve had. Highly recommended.",
    image: "https://api.carnivalcastle.com/uploads/testimoniImg/1779723513573-DSC_0942.JPG",
    avatar: null,
  },
  {
    _id: "cc-rev-5",
    name: "Karthik N",
    location: "Kondapur",
    category: "birthday",
    occasion: "Birthday Celebration",
    occasionEmoji: "🎉",
    theatre: "Royal Cinema",
    rating: 5,
    quote: "A very positive team and we had a whole lot of fun at our event. Amazing experience.",
    fullText: "A very positive team and we had a whole lot of fun at our event, amazing experience and can be recommended for small or family events.",
    image: "https://api.carnivalcastle.com/uploads/testimoniImg/1772734454578-1765619850014-image-1761459835980.jpg",
    avatar: null,
  },
];

const CATEGORIES = [
  { id: "all", label: "All Celebrations" },
  { id: "birthday", label: "Birthdays" },
  { id: "screening", label: "Movie Screenings" },
];

function Stars({ rating = 5, size = 13 }) {
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span
          key={n}
          style={{
            fontSize: size,
            color: n <= rating ? THEME.gold : "rgba(212, 175, 55, 0.2)",
            lineHeight: 1,
          }}
        >
          ★
        </span>
      ))}
    </div>
  );
}

// Circular offset calculation for 3D Coverflow
const getOffset = (i, active, total) => {
  let diff = (i - active) % total;
  if (diff < 0) diff += total;
  if (diff > total / 2) diff -= total;
  return diff;
};

export default function TestimonialsSection() {
  const [stories, setStories] = useState(REAL_STORIES);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== "undefined") return window.innerWidth < 768;
    return false;
  });
  const [windowWidth, setWindowWidth] = useState(() => {
    if (typeof window !== "undefined") return window.innerWidth;
    return 1200;
  });

  const touchStartX = useRef(0);
  const touchStartY = useRef(0);

  // Sync with API
  useEffect(() => {
    let isMounted = true;
    axios
      .post(
        "https://api.carnivalcastle.com/v1/carnivalApi/admin/testimonial/getalltestimoni?websiteFor=carnivalcastle"
      )
      .then((res) => {
        if (
          isMounted &&
          res.data?.success &&
          Array.isArray(res.data.testimoni) &&
          res.data.testimoni.length > 0
        ) {
          const apiList = res.data.testimoni
            .filter((t) => t.status === "active")
            .map((t, idx) => {
              const base = REAL_STORIES[idx % REAL_STORIES.length];
              const desc = t.description || base.fullText;
              return {
                _id: t._id || `api-${idx}`,
                name: t.name || base.name,
                location: t.location
                  ? t.location.charAt(0).toUpperCase() + t.location.slice(1)
                  : "Kondapur",
                category: (t.occasion?.toLowerCase().includes("movie") || t.occasion?.toLowerCase().includes("screening"))
                  ? "screening"
                  : "birthday",
                occasion: t.occasion || base.occasion,
                occasionEmoji: base.occasionEmoji,
                theatre: base.theatre,
                rating: Number(t.rating) || 5,
                quote: desc.length > 90 ? desc.slice(0, 90) + "..." : desc,
                fullText: desc,
                image: t.image.startsWith("http")
                  ? t.image
                  : `https://api.carnivalcastle.com/${t.image}`,
                avatar: t.profileImage
                  ? t.profileImage.startsWith("http")
                    ? t.profileImage
                    : `https://api.carnivalcastle.com/${t.profileImage}`
                  : null,
              };
            });
          if (apiList.length > 0) {
            setStories(apiList);
          }
        }
      })
      .catch((err) => {
        console.warn("Using verified celebration collection:", err.message);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  // Screen width detection
  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      setWindowWidth(w);
      setIsMobile(w < 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Filtered stories
  const filteredStories =
    selectedCategory === "all"
      ? stories
      : stories.filter((s) => s.category === selectedCategory);

  const displayStories = filteredStories.length > 0 ? filteredStories : stories;
  const total = displayStories.length;

  // Reset active index when category changes
  useEffect(() => {
    setActiveIndex(0);
    setProgress(0);
  }, [selectedCategory]);

  // Automatic Smooth Slider
  useEffect(() => {
    if (isHovered || total <= 1) {
      setProgress(0);
      return;
    }

    const interval = 100;
    const totalDuration = 4500;
    const step = (interval / totalDuration) * 100;

    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          setActiveIndex((old) => (old + 1) % total);
          return 0;
        }
        return prev + step;
      });
    }, interval);

    return () => clearInterval(timer);
  }, [isHovered, total]);

  const handlePrev = (e) => {
    e?.stopPropagation();
    setActiveIndex((prev) => (prev - 1 + total) % total);
    setProgress(0);
  };

  const handleNext = (e) => {
    e?.stopPropagation();
    setActiveIndex((prev) => (prev + 1) % total);
    setProgress(0);
  };

  // Touch Swipe Handlers for Mobile
  const handleTouchStart = (e) => {
    if (!e.touches || !e.touches[0]) return;
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e) => {
    if (!touchStartX.current) return;
    const endX = e.changedTouches && e.changedTouches[0] ? e.changedTouches[0].clientX : 0;
    const endY = e.changedTouches && e.changedTouches[0] ? e.changedTouches[0].clientY : 0;
    const diffX = touchStartX.current - endX;
    const diffY = touchStartY.current - endY;

    if (Math.abs(diffX) > 40 && Math.abs(diffX) > Math.abs(diffY)) {
      if (diffX > 0) {
        handleNext();
      } else {
        handlePrev();
      }
    }
    touchStartX.current = 0;
    touchStartY.current = 0;
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,600;0,700;1,400;1,600&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');

        /* ── SECTION: SOLID MATTE OBSIDIAN (NO WHITE, NO HEAVY GRADIENTS) ── */
        .ts-spotlight-sec {
          background-color: ${THEME.bg};
          padding: 96px 0 88px;
          position: relative;
          overflow: hidden;
          font-family: 'Plus Jakarta Sans', sans-serif;
          color: ${THEME.textMain};
        }

        /* Top Delicate Gold Accent Line */
        .ts-spotlight-sec::before {
          content: '';
          position: absolute;
          top: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 260px;
          height: 1.5px;
          background: linear-gradient(90deg, transparent, ${THEME.gold}, transparent);
          opacity: 0.75;
        }

        .ts-spotlight-container {
          max-width: 1260px;
          margin: 0 auto;
          padding: 0 24px;
          position: relative;
          z-index: 2;
        }

        /* ── 1. HEADER (EDITORIAL CINEMA MARQUEE) ── */
        .ts-spotlight-header {
          text-align: center;
          max-width: 700px;
          margin: 0 auto;
        }

        .ts-spotlight-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 3.5px;
          text-transform: uppercase;
          color: ${THEME.gold};
          margin-bottom: 14px;
        }

        .ts-eyebrow-line {
          width: 22px;
          height: 1px;
          background: ${THEME.gold};
          opacity: 0.5;
        }

        .ts-spotlight-h2 {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: clamp(32px, 4.4vw, 50px);
          font-weight: 700;
          line-height: 1.16;
          color: #FFFFFF;
          margin: 0 0 16px;
          letter-spacing: -0.5px;
        }

        .ts-spotlight-h2 em {
          font-family: 'Playfair Display', Georgia, serif;
          font-style: italic;
          font-weight: 500;
          color: ${THEME.goldLight};
        }

        .ts-spotlight-sub {
          font-size: 15px;
          color: ${THEME.textSub};
          line-height: 1.7;
          margin: 0 auto;
          max-width: 560px;
        }

        /* ── 2. TRUST STRIP (MATTE GRAPHITE PILL) ── */
        .ts-trust-bar {
          max-width: 960px;
          margin: 38px auto 40px;
          background: #121118;
          border: 1px solid rgba(212, 175, 55, 0.2);
          border-radius: 16px;
          padding: 22px 36px;
          display: grid;
          grid-template-columns: 1fr auto 1fr auto 1fr auto 1fr;
          align-items: center;
          gap: 16px;
          box-shadow: 0 12px 30px rgba(0, 0, 0, 0.45);
        }

        .ts-trust-node {
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }

        .ts-trust-val {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 32px;
          font-weight: 700;
          color: #F8ECD1;
          line-height: 1;
          letter-spacing: -0.5px;
        }

        .ts-trust-stars {
          margin: 4px 0 2px;
        }

        .ts-trust-lbl {
          font-size: 10.5px;
          font-weight: 700;
          letter-spacing: 2px;
          text-transform: uppercase;
          color: rgba(255, 255, 255, 0.52);
          margin-top: 5px;
        }

        .ts-trust-sep {
          width: 1px;
          height: 38px;
          background: rgba(212, 175, 55, 0.2);
        }

        /* ── OCCASION FILTER TABS ── */
        .ts-cat-tabs {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          margin-bottom: 30px;
          flex-wrap: wrap;
        }

        .ts-cat-tab {
          background: #14131B;
          border: 1px solid rgba(255, 255, 255, 0.12);
          color: rgba(255, 255, 255, 0.65);
          font-size: 12px;
          font-weight: 600;
          padding: 8px 18px;
          border-radius: 50px;
          cursor: pointer;
          transition: all 0.22s ease;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }

        .ts-cat-tab:hover {
          border-color: ${THEME.gold};
          color: #FFFFFF;
          background: rgba(212, 175, 55, 0.08);
        }

        .ts-cat-tab.active {
          background: #201E2B;
          border-color: ${THEME.gold};
          color: #FFFFFF;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
        }

        /* ── 3. 3D CENTER-SPOTLIGHT CAROUSEL STAGE ── */
        .ts-coverflow-stage {
          position: relative;
          height: 560px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 10px 0 20px;
          perspective: 1200px;
          overflow: hidden;
          touch-action: pan-y;
        }

        /* Each Story Card in the 3D Stage */
        .ts-coverflow-card {
          position: absolute;
          width: min(520px, 86vw);
          border-radius: 22px;
          background: ${THEME.cardBg};
          border: 1.5px solid ${THEME.cardBorder};
          transition: transform 0.65s cubic-bezier(0.25, 1, 0.5, 1),
                      opacity 0.65s ease,
                      box-shadow 0.65s ease,
                      border-color 0.65s ease;
          overflow: hidden;
          box-shadow: 0 16px 40px rgba(0, 0, 0, 0.6);
        }

        /* Active Center Card Highlight */
        .ts-coverflow-card.active-center {
          border-color: ${THEME.cardBorderActive};
          box-shadow: 0 25px 65px rgba(0, 0, 0, 0.8), 0 0 35px ${THEME.goldGlow};
        }

        /* Card Image Frame */
        .ts-card-img-wrap {
          position: relative;
          height: 250px;
          overflow: hidden;
          background-color: #0E0D13;
        }

        .ts-card-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transition: transform 0.6s ease;
        }

        .ts-coverflow-card.active-center:hover .ts-card-img {
          transform: scale(1.04);
        }

        /* Occasion Badge Top */
        .ts-card-badge {
          position: absolute;
          top: 14px;
          left: 14px;
          background: rgba(12, 11, 16, 0.88);
          border: 1px solid rgba(212, 175, 55, 0.35);
          color: #F8ECD1;
          font-size: 10.5px;
          font-weight: 700;
          letter-spacing: 0.6px;
          text-transform: uppercase;
          padding: 5px 12px;
          border-radius: 50px;
          backdrop-filter: blur(8px);
        }

        /* Venue Badge Bottom */
        .ts-card-venue {
          position: absolute;
          bottom: 14px;
          left: 14px;
          background: rgba(94, 14, 115, 0.88);
          border: 1px solid rgba(212, 175, 55, 0.25);
          color: #FFFFFF;
          font-size: 10px;
          font-weight: 600;
          padding: 4px 11px;
          border-radius: 4px;
          backdrop-filter: blur(6px);
        }

        /* Card Content Area */
        .ts-card-body {
          padding: 22px 24px 26px;
          display: flex;
          flex-direction: column;
          background: linear-gradient(180deg, #171620 0%, #121118 100%);
        }

        .ts-card-rating {
          margin-bottom: 12px;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .ts-card-rating-text {
          font-size: 11px;
          font-weight: 700;
          color: ${THEME.gold};
          letter-spacing: 0.5px;
        }

        .ts-card-quote {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 16px;
          font-style: italic;
          line-height: 1.62;
          color: #FFFFFF;
          margin: 0 0 18px;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .ts-card-divider {
          height: 1px;
          background: rgba(212, 175, 55, 0.2);
          margin-bottom: 16px;
        }

        .ts-card-author {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }

        .ts-author-info {
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: 0;
          flex: 1;
        }

        .ts-author-avatar {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          object-fit: cover;
          border: 1.5px solid ${THEME.gold};
          flex-shrink: 0;
        }

        .ts-author-placeholder {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: linear-gradient(135deg, #2D143E, #140A1C);
          color: #F8ECD1;
          font-family: 'Playfair Display', serif;
          font-size: 17px;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1.5px solid ${THEME.gold};
          flex-shrink: 0;
        }

        .ts-author-text-col {
          min-width: 0;
          flex: 1;
        }

        .ts-author-name {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 16px;
          font-weight: 700;
          color: #FFFFFF;
          margin: 0;
          line-height: 1.25;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .ts-author-meta {
          font-size: 11px;
          font-weight: 600;
          color: ${THEME.gold};
          letter-spacing: 0.6px;
          text-transform: uppercase;
          margin-top: 3px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .ts-verified-badge {
          font-size: 10px;
          font-weight: 700;
          color: #38A169;
          background: rgba(56, 161, 105, 0.12);
          border: 1px solid rgba(56, 161, 105, 0.3);
          padding: 3px 9px;
          border-radius: 50px;
          display: flex;
          align-items: center;
          gap: 4px;
          flex-shrink: 0;
        }

        /* ── 4. SLIDER CONTROLS (AUTOPLAY TIMER + ARROWS) ── */
        .ts-slider-controls {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-top: 14px;
          padding: 0 4px;
          flex-wrap: wrap;
          gap: 16px;
        }

        /* Auto-play Timer Visualizer */
        .ts-timer-wrap {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .ts-timer-label {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          color: ${THEME.textMuted};
        }

        .ts-timer-track {
          width: 120px;
          height: 3px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 10px;
          overflow: hidden;
        }

        .ts-timer-bar {
          height: 100%;
          background: linear-gradient(90deg, #8A12A8, ${THEME.gold});
          border-radius: 10px;
          transition: width 0.1s linear;
        }

        .ts-mobile-hint {
          display: none;
          font-size: 10.5px;
          font-weight: 600;
          color: rgba(212, 175, 55, 0.7);
          letter-spacing: 1px;
          text-transform: uppercase;
        }

        /* Navigation Buttons and Dots */
        .ts-nav-group {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .ts-nav-btn {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: #181720;
          border: 1.5px solid rgba(212, 175, 55, 0.3);
          color: #F8ECD1;
          font-size: 20px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.22s ease;
          outline: none;
        }

        .ts-nav-btn:hover {
          background: ${THEME.gold};
          color: #0C0B10;
          border-color: ${THEME.gold};
          transform: translateY(-2px);
          box-shadow: 0 4px 14px rgba(212, 175, 55, 0.4);
        }

        .ts-dots-group {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .ts-dot-pill {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.18);
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
          padding: 0;
        }

        .ts-dot-pill.active {
          width: 24px;
          border-radius: 4px;
          background: ${THEME.gold};
          box-shadow: 0 0 10px rgba(212, 175, 55, 0.5);
        }

        /* ── 5. PLATFORM ACCREDITATIONS (GOOGLE / FACEBOOK / JUSTDIAL) ── */
        .ts-accred-strip {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 36px;
          flex-wrap: wrap;
          margin-top: 50px;
          padding: 22px 34px;
          background: #121118;
          border: 1px solid rgba(212, 175, 55, 0.14);
          border-radius: 16px;
        }

        .ts-accred-item {
          text-align: center;
        }

        .ts-accred-source {
          font-size: 10px;
          font-weight: 700;
          color: rgba(255, 255, 255, 0.45);
          letter-spacing: 2px;
          text-transform: uppercase;
          margin-bottom: 4px;
        }

        .ts-accred-val {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
        }

        .ts-accred-score {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 21px;
          font-weight: 700;
          color: #FFFFFF;
        }

        .ts-accred-cnt {
          font-size: 10px;
          color: rgba(255, 255, 255, 0.35);
          margin-top: 2px;
        }

        .ts-accred-sep {
          width: 1px;
          height: 36px;
          background: rgba(255, 255, 255, 0.08);
        }

        /* ── RESPONSIVE BREAKPOINTS ── */
        @media (min-width: 768px) and (max-width: 1100px) {
          .ts-coverflow-card {
            width: min(460px, 64vw);
          }
          .ts-coverflow-stage {
            height: 520px;
          }
          .ts-card-img-wrap {
            height: 220px;
          }
          .ts-trust-bar {
            grid-template-columns: 1fr 1fr;
            gap: 18px;
            padding: 20px 24px;
          }
          .ts-trust-sep {
            display: none;
          }
        }

        @media (max-width: 991px) {
          .ts-trust-bar {
            grid-template-columns: 1fr 1fr;
            gap: 18px;
            padding: 20px 24px;
          }
          .ts-trust-sep {
            display: none;
          }
        }

        @media (max-width: 767px) {
          .ts-spotlight-sec {
            padding: 56px 0 46px;
          }

          .ts-spotlight-container {
            padding: 0 14px;
          }

          .ts-spotlight-eyebrow {
            font-size: 10px;
            letter-spacing: 2.5px;
            margin-bottom: 10px;
          }

          .ts-spotlight-h2 {
            font-size: 28px;
            margin: 0 0 12px;
          }

          .ts-spotlight-sub {
            font-size: 13.5px;
            line-height: 1.6;
            padding: 0 6px;
          }

          .ts-trust-bar {
            margin: 20px auto 26px;
            padding: 14px 12px;
            grid-template-columns: 1fr 1fr;
            gap: 12px 8px;
            border-radius: 12px;
          }

          .ts-trust-val {
            font-size: 24px;
          }

          .ts-trust-lbl {
            font-size: 9px;
            letter-spacing: 1px;
          }

          .ts-cat-tabs {
            gap: 6px;
            margin-bottom: 18px;
            padding: 0 4px;
          }

          .ts-cat-tab {
            font-size: 11px;
            padding: 6px 14px;
          }

          .ts-coverflow-stage {
            height: 440px;
            margin: 6px 0 14px;
          }

          .ts-coverflow-card {
            width: min(440px, 92vw);
            border-radius: 18px;
          }

          .ts-card-img-wrap {
            height: 185px;
          }

          .ts-card-badge {
            top: 10px;
            left: 10px;
            font-size: 9.5px;
            padding: 4px 10px;
          }

          .ts-card-venue {
            bottom: 10px;
            left: 10px;
            font-size: 9px;
            padding: 3px 9px;
          }

          .ts-card-body {
            padding: 14px 16px 16px;
          }

          .ts-card-rating {
            margin-bottom: 8px;
          }

          .ts-card-rating-text {
            font-size: 10px;
          }

          .ts-card-quote {
            font-size: 13.5px;
            line-height: 1.55;
            margin: 0 0 12px;
            -webkit-line-clamp: 3;
          }

          .ts-card-divider {
            margin-bottom: 12px;
          }

          .ts-card-author {
            gap: 8px;
          }

          .ts-author-info {
            gap: 9px;
          }

          .ts-author-avatar,
          .ts-author-placeholder {
            width: 38px;
            height: 38px;
            font-size: 15px;
          }

          .ts-author-name {
            font-size: 14px;
          }

          .ts-author-meta {
            font-size: 10px;
          }

          .ts-verified-badge {
            font-size: 9px;
            padding: 3px 8px;
          }

          .ts-slider-controls {
            flex-direction: column-reverse;
            align-items: center;
            gap: 12px;
            margin-top: 8px;
          }

          .ts-nav-group {
            gap: 12px;
          }

          .ts-nav-btn {
            width: 40px;
            height: 40px;
            font-size: 18px;
          }

          .ts-timer-wrap {
            gap: 8px;
          }

          .ts-timer-track {
            width: 90px;
          }

          .ts-timer-label {
            font-size: 10px;
          }

          .ts-mobile-hint {
            display: block;
          }

          .ts-accred-strip {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 6px;
            padding: 14px 6px;
            margin-top: 24px;
            border-radius: 12px;
          }

          .ts-accred-source {
            font-size: 8.5px;
            letter-spacing: 1px;
            margin-bottom: 2px;
          }

          .ts-accred-score {
            font-size: 17px;
          }

          .ts-accred-cnt {
            font-size: 8.5px;
            line-height: 1.2;
          }

          .ts-accred-sep {
            display: none;
          }
        }

        @media (max-width: 360px) {
          .ts-spotlight-h2 {
            font-size: 24px;
          }
          .ts-coverflow-stage {
            height: 420px;
          }
          .ts-card-img-wrap {
            height: 165px;
          }
          .ts-card-quote {
            font-size: 12.5px;
          }
          .ts-trust-val {
            font-size: 20px;
          }
          .ts-accred-strip {
            grid-template-columns: 1fr;
            gap: 10px;
          }
        }
      `}</style>

      <section
        className="ts-spotlight-sec cc-sec"
        id="testimonials"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="ts-spotlight-container">

          {/* ── 1. HEADER (EDITORIAL MARQUEE) ── */}
          <div className="ts-spotlight-header">
            <span className="ts-spotlight-eyebrow">
              <span className="ts-eyebrow-line" />
              ✦ GUEST STORIES ✦
              <span className="ts-eyebrow-line" />
            </span>

            <h2 className="ts-spotlight-h2">
              Real Celebrations, <br />
              <em>Real Magic</em>
            </h2>

            <p className="ts-spotlight-sub">
              Hear from our guests — their joy, their stories, their unforgettable moments at Carnival Castle.
            </p>
          </div>

          {/* ── 2. TRUST STRIP (MATTE GRAPHITE PILL) ── */}
          <div className="ts-trust-bar">
            {/* Stat 1 */}
            <div className="ts-trust-node">
              <div className="ts-trust-val">5.0</div>
              <div className="ts-trust-stars">
                <Stars rating={5} size={11} />
              </div>
              <div className="ts-trust-lbl">By Families</div>
            </div>

            <div className="ts-trust-sep" />

            {/* Stat 2 */}
            <div className="ts-trust-node">
              <div className="ts-trust-val">50k+</div>
              <div className="ts-trust-lbl">Happy Guests</div>
            </div>

            <div className="ts-trust-sep" />

            {/* Stat 3 */}
            <div className="ts-trust-node">
              <div className="ts-trust-val">5★</div>
              <div className="ts-trust-lbl">Rated By Couples</div>
            </div>

            <div className="ts-trust-sep" />

            {/* Stat 4 */}
            <div className="ts-trust-node">
              <div className="ts-trust-val">4.9★</div>
              <div className="ts-trust-lbl">Google Rating</div>
            </div>
          </div>

          {/* ── OCCASION FILTER TABS ── */}
          <div className="ts-cat-tabs">
            {CATEGORIES.map((cat) => {
              const count =
                cat.id === "all"
                  ? stories.length
                  : stories.filter((s) => s.category === cat.id).length;
              return (
                <button
                  key={cat.id}
                  type="button"
                  className={`ts-cat-tab ${selectedCategory === cat.id ? "active" : ""}`}
                  onClick={() => setSelectedCategory(cat.id)}
                >
                  <span>{cat.label}</span>
                  <span style={{ opacity: 0.6, fontSize: "11px" }}>({count})</span>
                </button>
              );
            })}
          </div>

          {/* ── 3. 3D CENTER-SPOTLIGHT CAROUSEL STAGE ── */}
          <div
            className="ts-coverflow-stage"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            {displayStories.map((story, i) => {
              const diff = getOffset(i, activeIndex, total);
              const isCenter = diff === 0;

              // Calculate 3D transforms based on position
              let transformStyle = "";
              let opacityStyle = 0;
              let zIndexStyle = 1;
              let pointerEventsStyle = "none";
              let cursorStyle = "default";

              if (isMobile) {
                // Mobile layout: clean horizontal slide with zero clip
                if (isCenter) {
                  transformStyle = "translateX(0) scale(1)";
                  opacityStyle = 1;
                  zIndexStyle = 10;
                  pointerEventsStyle = "auto";
                } else {
                  transformStyle = `translateX(${diff > 0 ? 100 : -100}%) scale(0.92)`;
                  opacityStyle = 0;
                  zIndexStyle = 1;
                }
              } else {
                // Desktop / Tablet 3D Coverflow layout
                const isTablet = windowWidth < 1100;
                const peekX = isTablet ? 54 : 64;
                const peekScale = isTablet ? 0.82 : 0.86;

                if (isCenter) {
                  transformStyle = "translateX(0) scale(1)";
                  opacityStyle = 1;
                  zIndexStyle = 10;
                  pointerEventsStyle = "auto";
                } else if (diff === 1) {
                  // Right card peeking
                  transformStyle = `translateX(${peekX}%) scale(${peekScale})`;
                  opacityStyle = 0.42;
                  zIndexStyle = 5;
                  pointerEventsStyle = "auto";
                  cursorStyle = "pointer";
                } else if (diff === -1) {
                  // Left card peeking
                  transformStyle = `translateX(-${peekX}%) scale(${peekScale})`;
                  opacityStyle = 0.42;
                  zIndexStyle = 5;
                  pointerEventsStyle = "auto";
                  cursorStyle = "pointer";
                } else {
                  // Distant cards
                  transformStyle = `translateX(${diff > 0 ? 115 : -115}%) scale(0.7)`;
                  opacityStyle = 0;
                  zIndexStyle = 1;
                }
              }

              return (
                <div
                  key={story._id}
                  className={`ts-coverflow-card ${isCenter ? "active-center" : ""}`}
                  style={{
                    transform: transformStyle,
                    opacity: opacityStyle,
                    zIndex: zIndexStyle,
                    pointerEvents: pointerEventsStyle,
                    cursor: cursorStyle,
                  }}
                  onClick={() => {
                    if (!isCenter) {
                      setActiveIndex(i);
                      setProgress(0);
                    }
                  }}
                >
                  {/* Photo Container */}
                  <div className="ts-card-img-wrap">
                    <img
                      src={story.image}
                      alt={`${story.name} celebration`}
                      className="ts-card-img"
                      loading="lazy"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = REAL_STORIES[0].image;
                      }}
                    />
                    <span className="ts-card-badge">
                      {story.occasionEmoji} {story.occasion}
                    </span>
                    <span className="ts-card-venue">
                      📍 {story.theatre}
                    </span>
                  </div>

                  {/* Body & Review */}
                  <div className="ts-card-body">
                    <div className="ts-card-rating">
                      <Stars rating={story.rating} size={13} />
                      <span className="ts-card-rating-text">5.0 Star Experience</span>
                    </div>

                    <p className="ts-card-quote">
                      "{story.fullText}"
                    </p>

                    <div className="ts-card-divider" />

                    <div className="ts-card-author">
                      <div className="ts-author-info">
                        {story.avatar ? (
                          <img
                            src={story.avatar}
                            alt={story.name}
                            className="ts-author-avatar"
                            loading="lazy"
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        ) : (
                          <div className="ts-author-placeholder">
                            {story.name.charAt(0).toUpperCase()}
                          </div>
                        )}

                        <div className="ts-author-text-col">
                          <h4 className="ts-author-name">{story.name}</h4>
                          <div className="ts-author-meta">
                            {story.occasion} · {story.location}
                          </div>
                        </div>
                      </div>

                      <span className="ts-verified-badge">
                        ✓ Verified
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* ── 4. SLIDER CONTROLS (AUTOPLAY TIMER + ARROWS) ── */}
          <div className="ts-slider-controls">
            {/* Auto-play Timer Visualizer & Mobile Gesture Hint */}
            <div className="ts-timer-wrap">
              <span className="ts-mobile-hint">👈 Swipe cards 👉</span>
              <span className="ts-timer-label">
                {isHovered ? "Paused" : "Auto Sliding"}
              </span>
              <div className="ts-timer-track">
                <div className="ts-timer-bar" style={{ width: `${progress}%` }} />
              </div>
            </div>

            {/* Navigation Buttons and Pagination Dots */}
            <div className="ts-nav-group">
              <button
                type="button"
                className="ts-nav-btn"
                onClick={handlePrev}
                aria-label="Previous story"
              >
                ‹
              </button>

              <div className="ts-dots-group">
                {displayStories.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`ts-dot-pill ${i === activeIndex ? "active" : ""}`}
                    onClick={() => {
                      setActiveIndex(i);
                      setProgress(0);
                    }}
                    aria-label={`Go to slide ${i + 1}`}
                  />
                ))}
              </div>

              <button
                type="button"
                className="ts-nav-btn"
                onClick={handleNext}
                aria-label="Next story"
              >
                ›
              </button>
            </div>
          </div>

          {/* ── 5. ACCREDITATIONS (GOOGLE / FACEBOOK / JUSTDIAL) ── */}
          <div className="ts-accred-strip">
            {[
              { source: "Google", score: "4.9", count: "200+ Verified Reviews" },
              { source: "Facebook", score: "4.8", count: "150+ Recommendations" },
              { source: "JustDial", score: "4.7", count: "80+ Ratings" },
            ].map((p, i) => (
              <React.Fragment key={i}>
                {i > 0 && <div className="ts-accred-sep" />}
                <div className="ts-accred-item">
                  <div className="ts-accred-source">{p.source}</div>
                  <div className="ts-accred-val">
                    <span className="ts-accred-score">{p.score}</span>
                    <Stars rating={5} size={11} />
                  </div>
                  <div className="ts-accred-cnt">{p.count}</div>
                </div>
              </React.Fragment>
            ))}
          </div>

        </div>
      </section>
    </>
  );
}